import React, { useState, useEffect } from 'react';
import { Zap, TrendingUp, TrendingDown, Minus, Info } from 'lucide-react';
import { auraApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const AuraScoreWidget = ({ userId = null, compact = false }) => {
  const { user } = useAuth();
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || user?.id;

  useEffect(() => {
    const fetchScore = async () => {
      if (!targetUserId) return;
      try {
        const response = await auraApi.getScore(targetUserId);
        setScore(response.data);
      } catch (error) {
        console.error('Failed to fetch Aura Score:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchScore();
  }, [targetUserId]);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 ${compact ? 'p-4' : 'p-6'} animate-pulse`}>
        <div className="h-20 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (!score) {
    return null;
  }

  const getScoreColor = (value) => {
    if (value >= 70) return 'text-emerald-500';
    if (value >= 40) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getRiskBadge = (risk) => {
    const colors = {
      low: 'bg-emerald-100 text-emerald-700',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700'
    };
    return colors[risk] || colors.medium;
  };

  const TrendIcon = score.score_trend > 0 ? TrendingUp : score.score_trend < 0 ? TrendingDown : Minus;
  const trendColor = score.score_trend > 0 ? 'text-emerald-500' : score.score_trend < 0 ? 'text-red-500' : 'text-gray-400';

  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className={`w-12 h-12 rounded-full border-4 ${getScoreColor(score.total_score).replace('text-', 'border-')} flex items-center justify-center`}>
            <Zap className={`w-5 h-5 ${getScoreColor(score.total_score)}`} />
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={`text-2xl font-bold ${getScoreColor(score.total_score)}`}>
              {score.total_score}
            </span>
            <TrendIcon className={`w-4 h-4 ${trendColor}`} />
          </div>
          <p className="text-xs text-gray-500">Aura Score</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-emerald-500" />
          <h3 className="font-semibold text-gray-900">Aura Score</h3>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info className="w-4 h-4 text-gray-400" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs text-sm">
                Your Aura Score reflects your engagement, activity, and contribution to the community.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Main Score */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-baseline gap-2">
            <span className={`text-5xl font-bold ${getScoreColor(score.total_score)}`}>
              {score.total_score}
            </span>
            <span className="text-gray-400 text-lg">/100</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <TrendIcon className={`w-4 h-4 ${trendColor}`} />
            <span className={`text-sm ${trendColor}`}>
              {score.score_trend > 0 ? '+' : ''}{score.score_trend} this week
            </span>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskBadge(score.risk_level)}`}>
          {score.risk_level.charAt(0).toUpperCase() + score.risk_level.slice(1)} Risk
        </span>
      </div>

      {/* Component Scores */}
      <div className="space-y-3">
        {[
          { label: 'Activity', value: score.activity_score, icon: '📝' },
          { label: 'Engagement', value: score.engagement_score, icon: '🎯' },
          { label: 'Social', value: score.social_score, icon: '🤝' },
          { label: 'Sentiment', value: score.sentiment_score, icon: '😊' },
          { label: 'Consistency', value: score.consistency_score, icon: '📅' },
        ].map((item) => (
          <div key={item.label}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-gray-600">
                {item.icon} {item.label}
              </span>
              <span className="font-medium text-gray-900">{item.value}</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  item.value >= 70 ? 'bg-emerald-500' :
                  item.value >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${item.value}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Insights */}
      {(score.strengths?.length > 0 || score.improvements?.length > 0) && (
        <div className="mt-6 pt-6 border-t border-gray-100">
          {score.strengths?.length > 0 && (
            <div className="mb-3">
              <p className="text-sm font-medium text-gray-900 mb-2">💪 Strengths</p>
              <div className="flex flex-wrap gap-2">
                {score.strengths.map((s, i) => (
                  <span key={i} className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          {score.improvements?.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-900 mb-2">🎯 Areas to Improve</p>
              <div className="flex flex-wrap gap-2">
                {score.improvements.map((s, i) => (
                  <span key={i} className="px-2 py-1 bg-yellow-50 text-yellow-700 text-xs rounded-full">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuraScoreWidget;
